import { FilterList } from "@mui/icons-material";
import { Checkbox, IconButton, Menu, MenuItem, Tooltip, ListItemIcon, ListItemText } from "@mui/material";
import { xor } from "lodash-es";
import { useState, useMemo, useCallback, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  title: string;
  filters: { id: string; label: string }[];
  activeFilters: string[];
  updateFilters: (filters: string[]) => void;
}

export function FleetConfigFilterButton({ title, filters, activeFilters, updateFilters }: Props) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const allIds = useMemo(() => filters.map((f) => f.id), [filters]);
  const allSelected = activeFilters.length === filters.length;
  const someSelected = activeFilters.length > 0 && !allSelected;

  const handleToggleAll = useCallback(() => {
    if (!allSelected) {
      updateFilters(xor(activeFilters, allIds));
    } else {
      updateFilters(allIds);
    }
  }, [allSelected, allIds, updateFilters, activeFilters]);

  const openMenu = useCallback((e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget), []);
const closeMenu = useCallback(() => setAnchorEl(null), []);

  return (
    <>
      <Tooltip title={title}>
        <IconButton
          size="small"
          aria-label={title}
          aria-controls={open ? "filter-menu" : undefined}
          aria-haspopup="true"
          onClick={openMenu}
        >
          <FilterList fontSize="small" color={someSelected ? "secondary" : "disabled"} />
        </IconButton>
      </Tooltip>
      <Menu
        id="filter-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{ style: { maxHeight: 400 } }}
      >
        <MenuItem onClick={handleToggleAll}>
          <ListItemIcon>
            <Checkbox size="small" checked={allSelected} indeterminate={someSelected} />
          </ListItemIcon>
          <ListItemText primary={t("label.selectAll")} />
        </MenuItem>
        {filters.map(({ id, label }) => (
          <MenuItem key={id} onClick={() => updateFilters([id])}>
            <ListItemIcon>
              <Checkbox size="small" checked={activeFilters.includes(id)} />
            </ListItemIcon>
            <ListItemText primary={label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
